import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { Typography } from 'src/components/new/Typography';
import { Button } from 'src/components/new/Button';
import { apiInstance } from 'src/api/apiInstance';

interface TermsSettings {
  version: string | null;
  tosUrl: string | null;
}

const PartnerAPIPage: React.FC = () => {
  // ── Form state ─────────────────────────────────────────────────────────────
  const [versionInput, setVersionInput] = useState('');
  const [tosUrlInput, setTosUrlInput] = useState('');

  // ── Current values stored in DB (shown below inputs) ──────────────────────
  const [currentSettings, setCurrentSettings] = useState<TermsSettings>({
    version: null,
    tosUrl: null,
  });

  // ── UI state ───────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // ── Fetch current settings on mount ───────────────────────────────────────
  useEffect(() => {
    apiInstance
      .get<TermsSettings>('/partners/settings/terms')
      .then((res) => {
        setCurrentSettings(res.data);
      })
      .catch(() => {
        setErrorMsg('Failed to load current settings. Please refresh and try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // ── Save handler ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    const trimmedVersion = versionInput.trim();
    const trimmedTosUrl = tosUrlInput.trim();

    if (!trimmedVersion && !trimmedTosUrl) {
      setErrorMsg('Please fill in at least one field before saving.');
      setSuccessMsg('');
      return;
    }

    setIsSaving(true);
    setSuccessMsg('');
    setErrorMsg('');

    const payload: Partial<{ version: string; tosUrl: string }> = {};
    if (trimmedVersion) payload.version = trimmedVersion;
    if (trimmedTosUrl) payload.tosUrl = trimmedTosUrl;

    try {
      await apiInstance.patch('/partners/settings/terms', payload);

      // Reflect the new values in the "current" display
      setCurrentSettings((prev) => ({
        version: trimmedVersion || prev.version,
        tosUrl: trimmedTosUrl || prev.tosUrl,
      }));

      // Clear the inputs so they're ready for the next update
      setVersionInput('');
      setTosUrlInput('');
      setSuccessMsg('Settings saved successfully.');
    } catch (err: any) {
      setErrorMsg(
        err?.response?.data?.message ?? 'Failed to save settings. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Page header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '1.5rem',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'ABC Arizona Mix',
            fontWeight: 300,
            fontSize: '2.8125rem',
            lineHeight: '3.25rem',
          }}
        >
          Partner API
        </Typography>
      </Box>

      {/* Tabs */}
      <Box>
        <Box sx={{ paddingLeft: '1.5rem' }}>
          <Tabs
            value={0}
            TabIndicatorProps={{ sx: { backgroundColor: '#436eb2' } }}
          >
            <Tab
              label="Terms of Service"
              sx={{
                textTransform: 'none',
                fontFamily: 'Instrument Sans',
                fontSize: '1rem',
                lineHeight: '1.25rem',
                color: '#45464F',
                '&.Mui-selected': { color: '#436eb2' },
              }}
            />
          </Tabs>
        </Box>

        <Divider />

        {/* Tab content */}
        <Box sx={{ padding: '1.5rem', maxWidth: '37.5rem' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography sx={{ fontSize: '0.9rem', color: '#666' }}>
                Loading current settings…
              </Typography>
            </Box>
          ) : (
            <>
              {successMsg && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
                  {successMsg}
                </Alert>
              )}
              {errorMsg && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>
                  {errorMsg}
                </Alert>
              )}

              {/* Version field */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Version"
                  value={versionInput}
                  onChange={(e) => setVersionInput(e.target.value)}
                  placeholder="e.g. 2026-06-01"
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 100 }}
                  sx={{ fontFamily: 'Instrument Sans' }}
                />
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: '#666',
                    mt: 0.5,
                    fontStyle: currentSettings.version ? 'normal' : 'italic',
                  }}
                >
                  {currentSettings.version
                    ? `Current value: ${currentSettings.version}`
                    : 'No version set yet'}
                </Typography>
              </Box>

              {/* Terms of Service URL field */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Terms of Service URL"
                  value={tosUrlInput}
                  onChange={(e) => setTosUrlInput(e.target.value)}
                  placeholder="https://…"
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 2048 }}
                  sx={{ fontFamily: 'Instrument Sans' }}
                />
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: '#666',
                    mt: 0.5,
                    fontStyle: currentSettings.tosUrl ? 'normal' : 'italic',
                    wordBreak: 'break-all',
                  }}
                >
                  {currentSettings.tosUrl
                    ? `Current value: ${currentSettings.tosUrl}`
                    : 'No URL set yet'}
                </Typography>
              </Box>

              <Button
                type="primary"
                onClick={handleSave}
                disabled={isSaving}
                sx={{ width: '10rem' }}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PartnerAPIPage;
